import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PlusIcon } from '@heroicons/react/24/outline';

import Navbar from '../../components/Navbar/Navbar';
import AssetsService from '../../services/AssetsService';
import { Location } from '../../services/models/assets';
import useLocationRole from '../../hooks/useLocationRole';

const ChooseSite = () => {
  const navigate = useNavigate();
  // A teammate only ever reaches sites someone else owns — creating one is not
  // theirs to do.
  const { isMemberOnly } = useLocationRole();

  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: () => AssetsService.getLocations(),
    select: (data) => data.locations,
  });

  const getStationLabel = (location: Location) => {
    const count = location.stations?.length ?? 0;
    if (count === 0) return 'Pas encore de stations';
    return `${count} station${count > 1 ? 's' : ''}`;
  };

  return (
    <div className="flex flex-col h-screen bg-off-white">
      <Navbar breadcrumbs={[{ label: 'Sites' }]} />
      <div className="self-stretch flex-1 inline-flex justify-center items-start">
        <div className="flex-1 self-stretch pt-16 inline-flex flex-col justify-start items-center gap-8 overflow-auto">
          <div className="w-full max-w-[1280px] px-4 flex flex-col justify-start items-start gap-8">
            <div className="self-stretch inline-flex justify-start items-start gap-4">
              <div className="flex-1 inline-flex flex-col justify-center items-start">
                <h1 className="text-neutral typo-h1">Vos sites</h1>
              </div>
            </div>
          </div>
          <div className="w-full max-w-[1280px] px-4 flex flex-col justify-start items-center gap-4">
            <div className="self-stretch inline-flex justify-start items-start gap-4 flex-wrap">
              {locations?.map((location) => (
                <button
                  key={location.id}
                  type="button"
                  className="flex-1 max-w-96 min-w-[280px] p-4 bg-white rounded-lg shadow-card outline outline-1 outline-offset-[-1px] outline-neutral-10 inline-flex flex-col justify-center items-start gap-2 overflow-hidden hover:shadow-md transition-shadow text-left"
                  onClick={() => navigate(`/site/${location.id}`)}
                >
                  <div className="self-stretch inline-flex justify-start items-center gap-4">
                    <div className="w-10 h-10 rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-10 inline-flex flex-col justify-center items-center overflow-hidden">
                      <img src="/site.svg" alt="" className="w-3.5 h-3.5" />
                    </div>
                    <div className="inline-flex flex-col justify-center items-start">
                      <span className="text-neutral typo-h3">
                        {location.name}
                      </span>
                      <span className="text-neutral-60 typo-small">
                        {getStationLabel(location)}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
              {!isMemberOnly && (
                <button
                  type="button"
                  className="flex-1 max-w-96 min-w-[280px] p-4 rounded-lg border-2 border-dashed border-neutral-20 inline-flex flex-col justify-center items-start gap-2 overflow-hidden hover:border-neutral-40 hover:bg-neutral-5 transition-all text-left"
                  onClick={() => navigate('/sites/new')}
                >
                  <div className="self-stretch inline-flex justify-start items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-neutral-5 inline-flex flex-col justify-center items-center overflow-hidden">
                      <PlusIcon className="w-4 h-4 text-neutral-40" />
                    </div>
                    <span className="text-neutral-40 typo-h3">Ajouter un site</span>
                  </div>
                </button>
              )}
              {isMemberOnly && (locations?.length ?? 0) === 0 && (
                <div className="flex-1 p-8 flex flex-col items-start gap-1">
                  <span className="text-neutral typo-h3">Aucun site partagé avec vous</span>
                  <span className="text-neutral-60 typo-small">
                    Dès qu'un propriétaire vous ajoute à son équipe, son site apparaîtra ici.
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChooseSite;
